import { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, CheckCircle2, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { FORMAT } from "@/lib/site";

const EMPTY = {
    brand: "",
    model: "",
    year: 2023,
    mileage: 0,
    fuel: "Petrol",
    transmission: "Automatic",
    horsepower: 0,
    price: 0,
    body_style: "Sedan",
    color: "",
    description: "",
    images: [],
    featured: false,
    sold: false,
};

function CarFormModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(() => initial ? { ...initial, images: initial.images || [] } : EMPTY);
    const [imgInput, setImgInput] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addImg = () => {
        if (!imgInput.trim()) return;
        set("images", [...form.images, imgInput.trim()]);
        setImgInput("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = {
                ...form,
                year: Number(form.year),
                mileage: Number(form.mileage),
                horsepower: Number(form.horsepower),
                price: Number(form.price),
            };
            if (initial?.id) {
                await api.put(`/admin/cars/${initial.id}`, payload);
            } else {
                await api.post("/admin/cars", payload);
            }
            onSaved();
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-surface border border-line rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                data-testid="car-form-modal"
            >
                <div className="flex items-center justify-between p-6 border-b border-line sticky top-0 bg-surface">
                    <h2 className="font-display text-2xl">{initial ? "Редактирай автомобил" : "Нов автомобил"}</h2>
                    <button type="button" onClick={onClose} className="p-2 text-ink-muted hover:text-gold">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 grid sm:grid-cols-2 gap-4">
                    {[
                        ["brand", "Марка", "text"],
                        ["model", "Модел", "text"],
                        ["year", "Година", "number"],
                        ["mileage", "Пробег (км)", "number"],
                        ["horsepower", "Мощност (к.с.)", "number"],
                        ["price", "Цена (EUR)", "number"],
                        ["color", "Цвят", "text"],
                    ].map(([k, label, type]) => (
                        <label key={k} className="block bg-surface2 border border-line rounded-xl px-4 py-3">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">{label}</span>
                            <input
                                type={type}
                                required={["brand", "model", "year", "price"].includes(k)}
                                value={form[k]}
                                onChange={(e) => set(k, e.target.value)}
                                className="w-full mt-1 bg-transparent text-sm focus:outline-none"
                                data-testid={`car-field-${k}`}
                            />
                        </label>
                    ))}
                    <Select label="Гориво" value={form.fuel} onChange={(v) => set("fuel", v)} options={["Petrol", "Diesel", "Hybrid", "Electric"]} />
                    <Select label="Скоростна кутия" value={form.transmission} onChange={(v) => set("transmission", v)} options={["Automatic", "Manual"]} />
                    <Select label="Каросерия" value={form.body_style} onChange={(v) => set("body_style", v)} options={["Sedan", "Coupe", "SUV", "Convertible", "Hatchback", "Wagon"]} />
                </div>

                <div className="px-6">
                    <label className="block bg-surface2 border border-line rounded-xl px-4 py-3">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">Описание</span>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            className="w-full mt-2 bg-transparent text-sm focus:outline-none resize-none"
                            data-testid="car-field-description"
                        />
                    </label>
                </div>

                <div className="p-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-subtle mb-2">Снимки (URL)</div>
                    <div className="flex gap-2 mb-3">
                        <input
                            value={imgInput}
                            onChange={(e) => setImgInput(e.target.value)}
                            placeholder="https://…"
                            className="flex-1 bg-surface2 border border-line rounded-xl px-4 py-3 text-sm focus:outline-none"
                            data-testid="car-image-input"
                        />
                        <button type="button" onClick={addImg} className="px-4 py-3 bg-gold text-bg rounded-xl text-sm font-medium" data-testid="car-image-add">
                            Добави
                        </button>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                        {form.images.map((url, i) => (
                            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-line">
                                <img src={url} alt="" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => set("images", form.images.filter((_, idx) => idx !== i))}
                                    className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full text-white flex items-center justify-center"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-6 flex flex-wrap gap-6">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.featured}
                            onChange={(e) => set("featured", e.target.checked)}
                            className="accent-gold"
                            data-testid="car-field-featured"
                        />
                        <span className="text-sm">Препоръчан</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.sold}
                            onChange={(e) => set("sold", e.target.checked)}
                            className="accent-gold"
                            data-testid="car-field-sold"
                        />
                        <span className="text-sm">Продаден</span>
                    </label>
                </div>

                {error && <div className="px-6 mt-4 text-sm text-red-400" data-testid="car-form-error">{error}</div>}

                <div className="p-6 flex justify-end gap-3 border-t border-line mt-4 sticky bottom-0 bg-surface">
                    <button type="button" onClick={onClose} className="px-6 py-3 border border-line rounded-full text-sm">
                        Отказ
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-3 bg-gold hover:bg-gold-light text-bg rounded-full text-sm font-medium disabled:opacity-60"
                        data-testid="car-form-submit"
                    >
                        {saving ? "Запис…" : initial ? "Запази промените" : "Създай автомобил"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <label className="block bg-surface2 border border-line rounded-xl px-4 py-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-surface2 text-sm focus:outline-none"
            >
                {options.map((o) => (
                    <option key={o}>{o}</option>
                ))}
            </select>
        </label>
    );
}

export default function AdminCarsPage() {
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/cars", { params: { include_sold: true, limit: 200 } })
            .then((r) => setCars(r.data || []))
            .finally(() => setLoading(false));
    };

    const loadSyncStatus = () => {
        api.get("/admin/sync/status").then((r) => setSyncStatus(r.data)).catch(() => {});
    };

    useEffect(() => {
        load();
        loadSyncStatus();
    }, []);

    const runSync = async () => {
        if (!window.confirm("Сигурни ли сте? Синхронизацията ще обнови всички автомобили с актуални данни от konstantin.mobile.bg. Това може да отнеме до 1 минута.")) return;
        setSyncing(true);
        setSyncResult(null);
        try {
            const { data } = await api.post("/admin/sync/mobile-bg");
            setSyncResult(data);
            loadSyncStatus();
            load();
        } catch (e) {
            setSyncResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setSyncing(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Сигурни ли сте, че искате да изтриете този автомобил?")) return;
        await api.delete(`/admin/cars/${id}`);
        load();
    };

    return (
        <div data-testid="admin-cars-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-4xl tracking-tightest">Автомобили</h1>
                    <p className="text-ink-muted mt-1">Управление на инвентара</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        onClick={runSync}
                        disabled={syncing}
                        className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold hover:bg-gold/10 text-gold px-5 py-3 rounded-full font-medium transition-all disabled:opacity-60"
                        data-testid="admin-sync-mobile-bg"
                    >
                        <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                        {syncing ? "Синхронизира се…" : "Синхронизирай от mobile.bg"}
                    </button>
                    <button
                        onClick={() => {
                            setEditing(null);
                            setShowForm(true);
                        }}
                        className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-bg px-5 py-3 rounded-full font-medium"
                        data-testid="admin-new-car"
                    >
                        <Plus size={16} /> Нов автомобил
                    </button>
                </div>
            </div>

            {/* Sync status banner */}
            <div className="mb-8 p-5 rounded-2xl border border-line bg-surface/60 flex items-start justify-between gap-4 flex-wrap" data-testid="sync-status-banner">
                <div className="flex-1 min-w-[240px]">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">
                        Автоматична синхронизация — mobile.bg
                    </div>
                    {syncStatus && syncStatus.started_at ? (
                        <div className="text-sm text-ink-muted">
                            Последен ран: <span className="text-ink">{new Date(syncStatus.started_at).toLocaleString("bg-BG")}</span>
                            {" • "}
                            Открити: <span className="text-gold">{syncStatus.discovered}</span>
                            {" • Добавени: "}<span className="text-gold">{syncStatus.inserted}</span>
                            {" • Обновени: "}<span className="text-gold">{syncStatus.updated}</span>
                            {syncStatus.marked_sold > 0 && (
                                <>{" • Продадени: "}<span className="text-gold">{syncStatus.marked_sold}</span></>
                            )}
                        </div>
                    ) : (
                        <div className="text-sm text-ink-muted">
                            Системата автоматично синхронизира всеки 24 часа. Не е изпълняван ръчно все още.
                        </div>
                    )}
                </div>
                {syncResult && !syncResult.error && (
                    <div className="text-xs text-gold flex items-center gap-2">
                        <CheckCircle2 size={14} /> Готово за {syncResult.duration_seconds?.toFixed(1)}s
                    </div>
                )}
                {syncResult?.error && (
                    <div className="text-xs text-red-400">{syncResult.error}</div>
                )}
            </div>

            {loading ? (
                <div className="text-ink-muted">Зареждане…</div>
            ) : (
                <div className="overflow-x-auto bg-surface border border-line rounded-2xl">
                    <table className="w-full text-sm">
                        <thead className="text-left text-ink-muted text-xs uppercase tracking-[0.25em] border-b border-line">
                            <tr>
                                <th className="p-4">Автомобил</th>
                                <th className="p-4">Година</th>
                                <th className="p-4">Цена</th>
                                <th className="p-4">Статус</th>
                                <th className="p-4 text-right">Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cars.map((c) => (
                                <tr key={c.id} className="border-t border-line hover:bg-surface2" data-testid={`admin-car-row-${c.id}`}>
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-bg shrink-0">
                                                {c.images?.[0] && <img src={c.images[0]} className="w-full h-full object-cover" alt="" />}
                                            </div>
                                            <div>
                                                <div className="font-medium">{c.brand} {c.model}</div>
                                                <div className="text-xs text-ink-subtle">{c.body_style}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 text-ink-muted">{c.year}</td>
                                    <td className="p-4 text-gold">{FORMAT.money(c.price)}</td>
                                    <td className="p-4">
                                        {c.sold ? (
                                            <span className="text-xs uppercase tracking-[0.2em] text-red-300">Продаден</span>
                                        ) : c.featured ? (
                                            <span className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-1">
                                                <CheckCircle2 size={12} /> Препоръчан
                                            </span>
                                        ) : (
                                            <span className="text-xs uppercase tracking-[0.2em] text-ink-muted">Активен</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-2">
                                            <button
                                                onClick={() => { setEditing(c); setShowForm(true); }}
                                                className="w-8 h-8 rounded-lg border border-line hover:border-gold hover:text-gold flex items-center justify-center"
                                                data-testid={`admin-car-edit-${c.id}`}
                                            >
                                                <Edit3 size={14} />
                                            </button>
                                            <button
                                                onClick={() => onDelete(c.id)}
                                                className="w-8 h-8 rounded-lg border border-line hover:border-red-500 hover:text-red-400 flex items-center justify-center"
                                                data-testid={`admin-car-delete-${c.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && (
                <CarFormModal
                    initial={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); load(); }}
                />
            )}
        </div>
    );
}
